import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../../Toast/context/useToast.context';
import { storySetupPdfQueryKeys } from './storySetupPdfQueryKeys';
import { updateSectionContentWithId, fetchStoryContent } from '../../../../graphQL/queries/publicationsQueries';
import { postJson } from '../../../../utils/fetchUtils';

export type FetchSectionContentParams = {
  storyId: string;
  contentSubstring: string;
};

export type SectionContentResult = {
  id: string;
  content: string;
};

export type UpdateSectionContentParams = {
  sectionId: string;
  updatedContent: string;
};

type UpdateSectionContentOptions = {
  storyId: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
};

type IStoryContentItem = {
  _id: string;
  content: string;
  contentHtml: string;
};

type IFetchSectionContentGraphQLResponse = {
  data?: {
    storyContent?: IStoryContentItem[];
  };
  errors?: { message: string }[];
};

type IUpdateSectionContentGraphQLResponse = {
  data?: {
    update_storyContents?: {
      affected_rows: number;
    };
  };
  errors?: { message: string }[];
};

/**
 * Fetches section content by story ID and content substring (section ID pattern)
 * Used to get current content before toggling page break class
 */
export const fetchSectionContent = async ({
  storyId,
  contentSubstring
}: FetchSectionContentParams): Promise<SectionContentResult> => {
  const response = await postJson<IFetchSectionContentGraphQLResponse>('/api/data/objects/stories/v1/graphql', {
    query: fetchStoryContent,
    variables: { storyId, contentSubstring }
  });

  if (response.data?.storyContent?.length && response.data.storyContent.length > 0) {
    const { _id, content } = response.data.storyContent[0];
    return { id: _id, content };
  }

  throw new Error(response.errors?.[0]?.message || 'Section content not found');
};

export const updateSectionContent = async ({
  sectionId,
  updatedContent
}: UpdateSectionContentParams): Promise<IUpdateSectionContentGraphQLResponse> => {
  const response = await postJson<IUpdateSectionContentGraphQLResponse>('/api/data/objects/stories/v1/graphql', {
    query: updateSectionContentWithId,
    variables: {
      id: sectionId,
      content: updatedContent,
      contentHtml: updatedContent
    }
  });

  if (response.errors) {
    throw new Error(response.errors[0]?.message || 'Failed to update section content');
  }

  return response;
};

/**
 * Mutation hook to update section content (e.g., for page breaks)
 * Automatically invalidates the story HTML content cache on success
 */
export function useUpdateSectionContent({ storyId, onSuccess, onError }: UpdateSectionContentOptions) {
  const queryClient = useQueryClient();
  const { toggleToast } = useToast();

  return useMutation({
    mutationFn: updateSectionContent,
    onSuccess: () => {
      // Invalidate the story HTML content cache to reflect the update
      void queryClient.invalidateQueries({
        queryKey: storySetupPdfQueryKeys.storyHtmlContent(storyId)
      });
      onSuccess?.();
    },
    onError: (error: Error) => {
      toggleToast({
        title: 'Failed to update page break',
        body: error.message,
        severity: 'error'
      });
      onError?.(error);
    }
  });
}
